import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import isEmpty from "lodash/isEmpty";
import NumberFormat from "react-number-format";
import sanitizeHtml from "sanitize-html";
import Select from "react-select";

// CKeditor
import CKEditor from "react-ckeditor-component";
import { editorReady } from "../../../utils/editor";

// Components
import Loader from "../../utils/Loader";
import FieldHelp from "../../utils/FieldHelp";
import PermalinkField from "../../utils/PermalinkField";
import DeleteConfirmationDialog from "../../utils/DeleteConfirmationDialog";
import ImageUploader from "../../utils/ImageUploader";
import CategorySearch from "./category-search/CategorySearch";
import Variant from "./variants/Variant";
import VariantSku from "./variants/VariantSku";

// State
import { userTokenUpdate, userLogout } from "../../../store/actions/user";
import { getTranslations } from "../../../store/actions/siteInfo";
import { REACT_APP_API_URL, REACT_APP_SITE_KEY } from "../../../shared/vars";

// CSS
import "../../../css/variants.css";
import "../../../css/selected-items.css";

// Utils
import { deleteTempImages } from "../../../utils/uploadHelpers";

class AdminProductForm extends Component {
	constructor(props) {
		super(props);
		this.state = {
			data: {
				id: null,
				title: "",
				permalink: !!props.match.params.permalink ? props.match.params.permalink : "",
				sku: "",
				description: "",
				images: [],
				inventory: "",
				weight: 1,
				price: "",
				pos_rebate: 0,
				pst_exempt: 0,
				gst_exempt: 0,
				shipping_exempt: 0,
				categories: [],
				active: 1,
				inactive_purchasable: 0,
				variants: [],
				variant_skus: []
			},

			isLoading: !!props.match.params.permalink ? true : false,
			addSucccess: !!props.location.state ? props.location.state.successMessage : "",
			updateLoading: false,
			updateErrors: {},
			updateSuccess: "",
			showDeleteConfirmationDialog: false,
			deletionInProgress: false,
			deleteErrors: "",
			deleteSuccess: "",
			promiseCollection: [],

			// Variant stuff
			updatedVariantsExist: false,
			variantAdditionInProgress: false
		};

		// Remove state from history object (success messages, etc)
		if (!!props.location.state) {
			props.history.replace({
				pathname: props.history.location.pathname
			});
		}

		props.getTranslations("add-product");

		// Bind all of the things
		this.handleKeyPress = this.handleKeyPress.bind(this);
		this.handleVariantTitleChange = this.handleVariantTitleChange.bind(this);
		this.handleVariantValueChange = this.handleVariantValueChange.bind(this);
		this.addVariantOption = this.addVariantOption.bind(this);
		this.deleteVariantOption = this.deleteVariantOption.bind(this);
		this.handleVariantSkuInputChange = this.handleVariantSkuInputChange.bind(this);

		this.handleImageAltUpdate = this.handleImageAltUpdate.bind(this);
	}

	componentDidMount() {
		if (this.state.data.permalink) {
			this.loadData();
		}
	}

	loadData() {
		this.setState(
			{
				isLoading: true
			},
			() => {
				const { permalink } = this.state.data;

				const apiEndpoint = "products/" + permalink;

				const AUTH_HEADER = {
					Authorization: REACT_APP_SITE_KEY + ":Bearer " + this.props.user.token
				};
				return axios
					.get(REACT_APP_API_URL + apiEndpoint, { headers: AUTH_HEADER })
					.then(response => {
						if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
							this.setState({ isLoading: false, error: response.data.message });
						} else {
							if (!response.data.product.variants) {
								response.data.product.variants = [];
							}
							if (!response.data.product.variant_skus) {
								response.data.product.variant_skus = [];
							}
							this.setState({ isLoading: false, data: { ...response.data.product } });
						}
					})
					.catch(err => {
						if (err.response) {
							this.setState({
								isLoading: false,
								error: err.response.data.errors ? err.response.data.errors : err.response.data.message
							});
						} else {
							this.setState({ isLoading: false, error: err.message });
						}
					});
			}
		);
	}

	// ===================================================================================
	//  IMAGES
	// ===================================================================================
	removeExistingImage(index) {
		let { images } = this.state.data;
		images.splice(index, 1);
		this.setState({
			data: {
				...this.state.data,
				images
			}
		});
	}

	handleImageAltUpdate(altText, index, isCurrent) {
		if (!isCurrent) return;

		let immutableState = this.state.data.images;

		// Alter the edited item as needed
		immutableState[index] = {
			...immutableState[index],
			alt: altText
		};

		// Set state with new object
		this.setState({
			data: {
				...this.state.data,
				images: immutableState
			}
		});
	}

	// ===================================================================================
	//  VALUE CHANGES
	// ===================================================================================
	handleKeyPress(event) {
		if (event.key === "Enter") {
			event.preventDefault();
			this.handleSubmit();
		}
	}

	handleInputChange(event) {
		const { id, value } = event.target;
		this.setState({
			data: {
				...this.state.data,
				[id]: value
			}
		});
	}

	editorChange(evt) {
		const newContent = evt.editor.getData();
		const newData = {
			...this.state.data,
			description: newContent
		};

		this.setState({ data: newData });
	}

	handleNumberChange(id, valueObject) {
		this.setState({
			data: {
				...this.state.data,
				[id]: valueObject.value
			}
		});
	}

	handleCheckboxClick(event) {
		const { id } = event.target;
		this.setState({
			data: {
				...this.state.data,
				[id]: !this.state.data[id]
			}
		});
	}

	// ===================================================================================
	//  VARIANTS
	// ===================================================================================
	handleVariantTitleChange(e, i) {
		const { value } = e.target;

		// Immutably clone specific variant item
		let variant = Object.assign({}, this.state.data.variants[i]);

		const variant_id = variant.id;

		// Set new title to item
		variant.title = value;

		// Set the variant to the cloned item
		let variants = this.state.data.variants;
		variants[i] = variant;

		// Get variant sku affected
		let skus = [...this.state.data.variant_skus];

		skus = skus.map(sku => {
			sku.sku_text.map(textItem => {
				// Ensure text piece is the one we're looking for
				if (textItem.variant_id !== variant_id) {
					return textItem;
				}

				textItem.variant_title = value;
				return textItem;
			});

			return sku;
		});

		// Finally, set the state
		this.setState({
			data: {
				...this.state.data,
				variant_skus: skus,
				variants
			}
		});
	}

	handleVariantValueChange(e, parentIndex, itemIndex, item) {
		const { value } = e.target;
		const newVariants = this.state.data.variants.map((variant, index) => {
			if (index === parentIndex) {
				return {
					...variant,
					items: variant.items.map((item, i) => {
						if (i !== itemIndex) {
							return item;
						}

						return {
							...item,
							title: value
						};
					})
				};
			}

			return variant;
		});

		const skus = this.state.data.variant_skus;

		skus.map(sku => {
			// Ensure sku has this id in it's list
			if (sku.ids.indexOf(item.id) < 0) {
				return sku;
			}

			sku.sku_text.map(textItem => {
				// Ensure text piece is the one we're looking for
				if (textItem.option_id !== item.id) {
					return textItem;
				}

				textItem.txt = value;
				return textItem;
			});

			return sku;
		});

		this.setState({
			data: {
				...this.state.data,
				variants: newVariants
			}
		});
	}

	handleVariantSkuInputChange(value, skuArrayIndex, skuItemType) {
		this.setState({
			updatedVariantsExist: true,
			data: {
				...this.state.data,
				variant_skus: this.state.data.variant_skus.map((item, index) => {
					if (index !== skuArrayIndex) return item;
					return {
						...item,
						[skuItemType]: value
					};
				})
			}
		});
	}

	addNewVariant() {
		const { permalink, sku } = this.state.data;
		const data = {
			type: "variant",
			action: "add",
			base_sku: sku,
			variant_data: this.state.data.variant_skus
		};

		this.setState({
			variantAdditionInProgress: true,
			updateErrors: {
				...this.state.updateErrors,
				variants: ""
			}
		});

		let endPointUrl = REACT_APP_API_URL + "variants/";
		if (!!this.state.data.id) {
			endPointUrl += permalink;
		}

		const AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + this.props.user.token
		};
		return axios
			.post(endPointUrl, data, { headers: AUTH_HEADER })
			.then(response => {
				if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: !isEmpty(response.data.errors) ? response.data.errors : response.data.message
						}
					});
				} else {
					// Add a new variant and a single blank option
					this.setState({
						data: {
							...this.state.data,
							variant_skus: response.data.variant_skus,
							variants: [
								...this.state.data.variants,
								{
									id: response.data.new.variant_id,
									title: "",
									new_value: 1,
									items: [{ id: response.data.new.option_id, title: "", new_value: 1 }]
								}
							]
						},
						variantAdditionInProgress: false,
						updatedVariantsExist: true
					});
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: err.response.data.error ? err.response.data.error : err.response.data.message
						}
					});
				} else {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: { ...this.state.updateErrors, variants: err.message }
					});
				}
			});
	}

	addVariantOption(variantArrayIndex) {
		const { id, sku, variants, variant_skus } = this.state.data;
		const variant = variants[variantArrayIndex];

		// Do not use permalink if product is new
		const permalink = id === null ? "" : this.state.data.permalink;

		const data = {
			type: "option",
			action: "add",
			base_sku: sku,
			variant_id: variant.id,
			variant_title: variant.title,
			variant_data: variant_skus
		};

		this.setState({
			variantAdditionInProgress: true,
			updateErrors: {
				...this.state.updateErrors,
				variants: ""
			}
		});

		const AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + this.props.user.token
		};
		return axios
			.post(REACT_APP_API_URL + "variants/" + permalink, data, { headers: AUTH_HEADER })
			.then(response => {
				if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: !isEmpty(response.data.errors) ? response.data.errors : response.data.message
						}
					});
				} else {
					// Add a new variant and a single blank option
					this.setState({
						data: {
							...this.state.data,
							variant_skus: response.data.variant_skus,
							variants: this.state.data.variants.map((item, index) => {
								if (index !== variantArrayIndex) return item;

								return {
									...item,
									items: [
										...item.items,
										{
											id: response.data.new.option_id,
											title: "",
											new_value: 1
										}
									]
								};
							})
						},
						variantAdditionInProgress: false,
						updatedVariantsExist: true
					});
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: err.response.data.error ? err.response.data.error : err.response.data.message
						}
					});
				} else {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: err.message
						}
					});
				}
			});
	}

	deleteVariantOption(variantArrayIndex, optionIndex) {
		const { permalink, sku, variants, variant_skus } = this.state.data;
		const variant = variants[variantArrayIndex];
		const option = variant.items[optionIndex];

		const data = {
			type: "option",
			action: "delete",
			base_sku: sku,
			option_id: option.id,
			variant_data: variant_skus
		};

		this.setState({
			variantAdditionInProgress: true,
			updateErrors: {
				...this.state.updateErrors,
				variants: ""
			}
		});

		const AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + this.props.user.token
		};
		return axios
			.post(REACT_APP_API_URL + "variants/" + permalink, data, { headers: AUTH_HEADER })
			.then(response => {
				if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: !isEmpty(response.data.errors) ? response.data.errors : response.data.message
						}
					});
				} else {
					let { variants } = this.state.data;
					let { items } = variants[variantArrayIndex];

					// Delete the item from the options list
					items = items.slice(0, optionIndex).concat(items.slice(optionIndex + 1));

					// If there are no options left, delete the variant entirely
					const newVariants = !items.length
						? variants.slice(0, variantArrayIndex).concat(variants.slice(variantArrayIndex + 1))
						: // Or just set the new options list with the one item removed
						this.state.data.variants.map((item, index) => {
							if (index !== variantArrayIndex) return item;
							return {
								...item,
								items
							};
						});

					// Delete the specific item from the variant
					this.setState({
						data: {
							...this.state.data,
							variant_skus: response.data.variant_skus,
							variants: newVariants
						},
						variantAdditionInProgress: false,
						updatedVariantsExist: true
					});
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: err.response.data.error ? err.response.data.error : err.response.data.message
						}
					});
				} else {
					this.setState({
						variantAdditionInProgress: false,
						updateErrors: {
							...this.state.updateErrors,
							variants: err.message
						}
					});
				}
			});
	}

	// ===================================================================================
	//  SUBMIT
	// ===================================================================================
	handleSubmit() {
		// Set the state to let everything know that submitting is happening
		// We need to wait for this to actually complete and utilize the callback so any uploaders
		// actually know that they should begin uploading
		this.setState(
			{
				updateSuccess: "",
				updateErrors: {},
				updateLoading: true
			},
			() => {
				// console.log(this.state.promiseCollection.length, this.state.promiseCollection)

				// Wait until all uploader promises have completed
				Promise.all(this.state.promiseCollection)
					.then(resolves => {
						// console.log('ok parent:', resolves)

						this.setState({
							promiseCollection: []
						});

						this.completeSubmit(resolves);
					})
					.catch(err => {
						// console.log('err parent:', err)
						this.setState({
							updateLoading: false,
							promiseCollection: []
						});
					});
			}
		);
	}

	completeSubmit(resolves) {
		const { user } = this.props;

		// Put data together
		const { data } = this.state;

		let imageResults = [];
		resolves.forEach(imgUploader => {
			if (imgUploader === null) return;
			imgUploader.forEach(img => {
				imageResults.push(img.results);
			});
		});
		data.new_images = imageResults;
		data.updated_variants = this.state.updatedVariantsExist;

		// console.log('completeSubmit', resolves)
		console.log('completeSubmit', data)
		// return

		const token = user.token;
		const AUTH_HEADER = { Authorization: REACT_APP_SITE_KEY + ":Bearer " + token };
		return axios
			.post(REACT_APP_API_URL + "products/chunked", data, { headers: AUTH_HEADER })
			.then(response => {
				// Check user token
				console.log(response)
				if (!!response.data.token) {
					this.props.userTokenUpdate("admin", response.data.token);
				} else {
					this.props.userLogout("admin");
				}

				if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
					// Create error object
					let errorObj = "";
					if (!isEmpty(response.data.errors)) {
						errorObj = response.data.errors;
					} else if (!isEmpty(response.data.error)) {
						errorObj = { general: response.data.error };
					} else {
						errorObj = { general: response.data.message };
					}

					this.setState({
						updateLoading: false,
						updateErrors: errorObj
					});

					deleteTempImages(imageResults, this.props.user.token).catch(err => {
						console.log(err);
					});
				} else {
					// console.log(data.id)

					// Redirect to edit route
					if (!data.id) {
						this.props.history.push({
							pathname: "/admin/products/edit/" + response.data.permalink,
							state: {
								successMessage: response.data.message
							}
						});
					}

					this.setState({
						updateLoading: false,
						updateSuccess: response.data.message
					});

					// Reload data
					this.loadData();
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						updateLoading: false,
						updateErrors: {
							general: err.response.data.error ? err.response.data.error : err.response.data.message
						}
					});
				} else {
					this.setState({
						updateLoading: false,
						updateErrors: { general: err.message }
					});
				}

				deleteTempImages(imageResults, this.props.user.token).catch(err => {
					console.log(err);
				});
			});
	}

	handleDelete(id) {
		const { user } = this.props;

		this.setState({
			deletionInProgress: true,
			deleteErrors: {},
			deleteSuccess: ""
		});

		const token = user.token;
		const AUTH_HEADER = { Authorization: REACT_APP_SITE_KEY + ":Bearer " + token };
		return axios
			.delete(REACT_APP_API_URL + "products/" + id, { headers: AUTH_HEADER })
			.then(response => {
				if (!!response.data.token) {
					this.props.userTokenUpdate("admin", response.data.token);
				}

				if (response.status !== 200 || (response.data.status && response.data.status !== 200)) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: !isEmpty(response.data.errors) ? response.data.errors : response.data.message
					});
				} else {
					this.setState({
						deletionInProgress: false,
						deleteSuccess: response.data.message
					});
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.response.data.error ? err.response.data.error : err.response.data.message
					});
				} else {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.message
					});
				}
			});
	}

	handleDeleteRedirect() {
		this.props.history.push("/admin/products/edit");
	}

	// ===================================================================================
	//  RENDER
	// ===================================================================================
	render() {
		const { translations } = this.props;
		const { addSucccess, updateLoading, deletionInProgress, updateSuccess, updateErrors, promiseCollection, data } = this.state;
		const { id, title, permalink, sku, description, images, inventory, weight, price, pos_rebate, pst_exempt, gst_exempt, shipping_exempt, categories, active, variants, variant_skus, inactive_purchasable } = data;
		const shipping_options = [
			{ id: 0, title: "Has Shipping" },
			{ id: 1, title: "Pickup Item (no shipping)" },
			{ id: 2, title: "Free Shipping Item" }
		];
		return (
			<div>
				{!this.props.match.params.permalink && <h1>Add Product</h1>}

				{/* Loading */}
				{this.state.isLoading ? (
					<div className="pt2 pb2">
						<Loader />
					</div>
				) : (
						// Show the form
						<div>
							{!!addSucccess && <div className="success msg_animate">{addSucccess}</div>}

							{!this.props.match.params.permalink || id ? (
								<div>
									{!!id && <input type="hidden" id="id" value={id} />}

									{!!id && (
										<div className="form-field product-url-display">
											<label>Product Url:</label>
											{!!categories && categories.length ? categories[0].permalink : ""}/product/{permalink}
										</div>
									)}

									<div className="form-field">
										<label>Title:</label>

										{updateErrors.title && <div className="error msg_animate">{updateErrors.title}</div>}
										<input type="text" id="title" value={title} maxLength="255" onKeyPress={e => this.handleKeyPress(e)} onChange={e => this.handleInputChange(e)} />
									</div>

									<div className="form-field">
										<label>Permalink:</label>

										{updateErrors.permalink && <div className="error msg_animate">{updateErrors.permalink}</div>}
										<PermalinkField
											tableName={"products"}
											id={id}
											value={permalink}
											controlFieldValue={title}
											fieldError={updateErrors.permalink}
											handleSubmit={this.handleSubmit.bind(this)}
											handleChange={res =>
												this.setState({
													data: { ...this.state.data, permalink: res }
												})
											}
										/>
									</div>

									<div className="form-field">
										<label>Sku:</label>

										{updateErrors.sku && <div className="error msg_animate">{updateErrors.sku}</div>}
										<input type="text" id="sku" value={sku} maxLength="25" onKeyPress={e => this.handleKeyPress(e)} onChange={e => this.handleInputChange(e)} />
									</div>

									<div className="form-field">
										<label>Description:</label>

										{!!updateErrors.description && <div className="error msg_animate">{updateErrors.description}</div>}
										<CKEditor
											scriptUrl="/ckeditor/ckeditor.js"
											content={description}
											config={{
												filebrowserBrowseUrl: "/file-browser",
												uploadUrl: REACT_APP_API_URL + `files`
											}}
											events={{
												instanceReady: editorReady.bind(this),
												change: this.editorChange.bind(this)
											}}
										/>
									</div>

									<div className="form-field">
										<label>Images:</label>

										{!!updateErrors.images && <div className="error msg_animate">{updateErrors.images}</div>}
										<ImageUploader
											loading={updateLoading}
											clear={!!updateSuccess}
											required={false}
											multiple={true}
											allowMultiple={true}
											uploadAssetType={"product"}
											addPromise={promise => promiseCollection.push(promise)}
											handleExistingImageRemoval={index => this.removeExistingImage(index)}
											initialImages={images.length ? images : []}
											imageProperty={"img"}
											toolbarTypes={{
												alt: {
													update: this.handleImageAltUpdate
												}
											}}
										/>
									</div>

									<div className="form-field half-field clear-float">
										<FieldHelp label={"Inventory"} helpItem={translations.inventory_help} />

										{!!updateErrors.inventory && <div className="error msg_animate">{updateErrors.inventory}</div>}
										<NumberFormat value={inventory} placeholder="" onKeyPress={e => this.handleKeyPress(e)} onValueChange={e => this.handleNumberChange("inventory", e)} />
									</div>

									<div className="form-field half-field clear-float">
										<label>Weight (lbs):</label>

										{!!updateErrors.weight && <div className="error msg_animate">{updateErrors.weight}</div>}
										<NumberFormat value={weight} placeholder="" onKeyPress={e => this.handleKeyPress(e)} onValueChange={e => this.handleNumberChange("weight", e)} />
									</div>

									<div className="form-field half-field clear-float">
										<label>Price:</label>

										{!!updateErrors.price && <div className="error msg_animate">{updateErrors.price}</div>}
										<NumberFormat value={price} placeholder="0.00" prefix={"$"} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onKeyPress={e => this.handleKeyPress(e)} onValueChange={e => this.handleNumberChange("price", e)} />
									</div>



									<div className="floated-field-container">
										<div className="form-field auto-width-field">
											{!!updateErrors.pos_rebate && <div className="error msg_animate">{updateErrors.pos_rebate}</div>}
											<div className="custom-label checkbox-label no-bottom-margin">
												<input type="checkbox" id="pos_rebate" value="1" checked={pos_rebate} onChange={e => this.handleCheckboxClick(e)} />
												<label htmlFor="pos_rebate">POS Rebate</label>
											</div>
										</div>
										<div className="form-field auto-width-field">
											{!!updateErrors.pst_exempt && <div className="error msg_animate">{updateErrors.pst_exempt}</div>}
											<div className="custom-label checkbox-label no-bottom-margin">
												<input type="checkbox" id="pst_exempt" value="1" checked={pst_exempt} onChange={e => this.handleCheckboxClick(e)} />
												<label htmlFor="pst_exempt">PST Exempt</label>
											</div>
										</div>
										<div className="form-field auto-width-field">
											{!!updateErrors.gst_exempt && <div className="error msg_animate">{updateErrors.gst_exempt}</div>}
											<div className="custom-label checkbox-label no-bottom-margin">
												<input type="checkbox" id="gst_exempt" value="1" checked={gst_exempt} onChange={e => this.handleCheckboxClick(e)} />
												<label htmlFor="gst_exempt">GST Exempt</label>
											</div>
										</div>
									</div>

									<div className="floated-field-container">
										<div className="form-field auto-width-field">
											<div className="custom-label checkbox-label no-bottom-margin">
												<input type="checkbox" id="shipping_exempt" checked={shipping_exempt} onChange={e => this.handleCheckboxClick(e)} />
												<label htmlFor="shipping_exempt">Pickup Item (no shipping)</label>
											</div>
										</div>
									</div>

									{/* CATEGORIES */}
									<div className="form-field half-field clear-float">
										<label>Categories:</label>

										{!!updateErrors.categories && <div className="error msg_animate">{updateErrors.categories}</div>}
										<CategorySearch
											currentlySelected={categories ? categories : []}
											updateHandler={selections =>
												this.setState({
													...this.state,
													data: {
														...this.state.data,
														categories: selections
													}
												})
											}
										/>

										<div className="selected-item-wrapper">
											{!!categories &&
												categories.map((category, i) => {
													return (
														<div key={i} className="selected-item">
															<div className="txt">{category.title}</div>
															<input type="hidden" name="categories[]" value={category.id} />
															<span
																className="icon-cross delete-category"
																onClick={() => {
																	this.setState({
																		...this.state,
																		data: {
																			...this.state.data,
																			categories: this.state.data.categories.filter((_, catIndex) => catIndex !== i)
																		}
																	});
																}}
															/>
														</div>
													);
												})}
										</div>
									</div>

									{/* ACTIVE */}
									<div className="form-field">
										{!!updateErrors.active && <div className="error msg_animate">{updateErrors.active}</div>}
										<div className="custom-label checkbox-label no-bottom-margin">
											<input type="checkbox" id="active" value="1" checked={active} onChange={e => this.handleCheckboxClick(e)} />
											<label htmlFor="active">Active</label>
										</div>
									</div>

									{/* PURCHASABLE WHILE INACTIVE */}
									<div className="form-field" style={{display: 'flex', alignItems: 'center'}}>
										{!!updateErrors.inactive_purchasable && <div className="error msg_animate">{updateErrors.inactive_purchasable}</div>}
										<div className="custom-label checkbox-label no-bottom-margin">
											<input type="checkbox" id="inactive_purchasable" value="1" checked={inactive_purchasable} onChange={e => this.handleCheckboxClick(e)} />
											<label htmlFor="inactive_purchasable">Can Still Be Purchased While Inactive</label>
										</div>
										<FieldHelp noMargin label={"What is this?"} helpItem={{txt: 'By checking this, you will allow inactive products to still be purchased if you send someone the product link.'}} />
									</div>

									{/* VARIANTS */}
									<div className="form-field">
										<FieldHelp label={"Variants"} helpItem={translations.variant_help} />

										{!!updateErrors.variants && (
											<div
												className="error msg_animate"
												dangerouslySetInnerHTML={{
													__html: sanitizeHtml(updateErrors.variants)
												}}
											/>
										)}
										<div className="">
											<div className={"btn" + (this.state.variantAdditionInProgress ? " loading" : "")} disabled={this.state.variantAdditionInProgress} onClick={() => this.addNewVariant()}>
												Add Variant
										</div>
										</div>

										{variants.map((variant, i) => {
											return <Variant key={i} variant={variant} variantIndex={i} variantAdditionInProgress={this.state.variantAdditionInProgress} changeVariantTitle={this.handleVariantTitleChange} changeVariantValue={this.handleVariantValueChange} addVariantOption={this.addVariantOption} deleteVariantOption={this.deleteVariantOption} />;
										})}
									</div>

									{/* VARIANT SKUS */}
									<div className="form-field">
										<FieldHelp label={"Variant Skus"} helpItem={translations.variant_sku_help} />

										{updateErrors.variant_skus && <div className="error msg_animate">{updateErrors.variants}</div>}

										<div className="sku-variant-collection">
											<div className="variant-wrapper">
												<div className="varient-item-titles">
													<div className="variant-item-title" />
													<div className="varient-attribute-titles">
														<div className="variant-attribute">
															<FieldHelp label={"Sku"} helpItem={translations.variant_sku_sku_help} />
														</div>
														<div className="variant-price">
															<FieldHelp label={"Price"} helpItem={translations.variant_sku_price_help} />
														</div>
														<div className="variant-inventory">
															<FieldHelp label={"Inventory"} helpItem={translations.variant_sku_inventory_help} />
														</div>
													</div>
												</div>
											</div>
										</div>

										<div className="sku-variant-collection">
											<div className="variant-wrapper">
												{variant_skus.map((variant_sku, i) => {
													return <VariantSku key={i} variant_sku={variant_sku} skuArrayIndex={i} handleKeyPress={this.handleKeyPress} handleVariantSkuInputChange={this.handleVariantSkuInputChange} />;
												})}
											</div>
										</div>
									</div>

									{updateSuccess && <div className="success msg_animate">{updateSuccess}</div>}

									<div className="btn_container">
										{!!updateErrors.general && (
											<div
												className="error msg_animate"
												dangerouslySetInnerHTML={{
													__html: sanitizeHtml(updateErrors.general)
												}}
											/>
										)}

										<button className={"btn submit-btn" + (updateLoading ? " loading" : "")} onClick={() => this.handleSubmit()} disabled={updateLoading}>
											<span>{!!id ? "Update" : "Create"}</span>
										</button>
										{!!id && (
											<button
												className={"btn delete-btn" + (deletionInProgress ? " loading" : "")}
												onClick={() =>
													this.setState({
														showDeleteConfirmationDialog: true
													})
												}
												disabled={updateLoading || deletionInProgress}
											>
												<span>Delete</span>
											</button>
										)}

										<DeleteConfirmationDialog show={this.state.showDeleteConfirmationDialog} deletionInProgress={this.state.deletionInProgress} id={id} itemTitle={title} handleDelete={this.handleDelete.bind(this)} handleDeleteRedirect={this.handleDeleteRedirect.bind(this)} handleClose={() => this.setState({ showDeleteConfirmationDialog: false })} deleteErrors={this.state.deleteErrors} deleteSuccess={this.state.deleteSuccess} />
									</div>
								</div>
							) : (
									<div>Product not found</div>
								)}
						</div>
					)}
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		user: state.user.admin,
		translations: state.siteInfo.translations
	};
};

export default connect(mapStateToProps, { userTokenUpdate, userLogout, getTranslations })(AdminProductForm);



// WEBPACK FOOTER //
// ./src/components/admin/products/AdminProductForm.js